import { TextInputField } from '@sainsburys-tech/luna-form'
import { Body1 } from '@sainsburys-tech/luna-typography'
import { OutlinedButton } from '@sainsburys-tech/luna-button'
import { IProductLookup } from '../../../../../types/ComponentTypes/Components'
import { useEffect, useState } from 'react'
import productApi from '../../../../../api/productApi'
import { useApiClient } from '../../../../../context/AppContext'
import { useComponentContext } from '../../ComponentContext'
import useCurrentStore from '../../../../../hooks/useCurrentStore'
import ComponentSwitch from '../..'
import { Container } from '@sainsburys-tech/luna-grid'
import { IProductDetails } from '../../../../../types'
import { ScanButton } from '@jsainsburyplc/retail-search-scanner'
import removeSpecialCharacters from '../../../../../utils/helper/stringHelper'
import { MIN_SKU_LENGTH, MAX_SKU_LENGTH } from '../../../../../utils/constants'

function ProductLookupComponent({
  key,
  id,
  productName,
  price,
  quantity,
  putBackOnSale,
  defaultValue,
}: IProductLookup) {

  const apiClient = useApiClient()
  const { handleUpdate } = useComponentContext()

  const storeLocationId = useCurrentStore().storeCode

  const [value, setValue] = useState(defaultValue ? JSON.parse(defaultValue) : null)
  const [lookupField, setLookupField] = useState('')
  const [lookupButtonClicked, setLookupButtonClicked] = useState(false)
  const [productDetails, setProductDetails] = useState<IProductDetails | null>(null)
  const [productNotFoundError, setProductNotFoundError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    handleUpdate({ id: id, value: value == undefined ? value : JSON.stringify(value) })
  }, [value])

  useEffect(() => {
    if (lookupButtonClicked) {
      handleButtonClick()
      setLookupButtonClicked(false)
    }
  }, [lookupButtonClicked])

  const handleLookupChange = (e: any) => {
    setLookupField(e.target.value)
  }

  const handleButtonClick = async () => {
    if (lookupField === '') {
      setProductNotFoundError(`Please enter a valid SKU, this should be ${MIN_SKU_LENGTH} to ${MAX_SKU_LENGTH} digits.`)

      return
    }

    if (lookupField) {
      const product = await productApi.GetProduct(apiClient, removeSpecialCharacters(lookupField), storeLocationId)
      const productInfo = { productName: product.name, sku: product.sku, price: product.price }
      setProductDetails(productInfo)
      setValue(productInfo)

      if ((lookupField.length < MIN_SKU_LENGTH && !product.sku)
        || (lookupField.length > MAX_SKU_LENGTH && !product.sku)) {
        setProductNotFoundError(`Please enter a valid SKU, this should be ${MIN_SKU_LENGTH} to ${MAX_SKU_LENGTH} digits.`)

        return
      }

      if (!product.sku) {
        setProductNotFoundError(`Product SKU or EAN ${lookupField} not found, please try again or enter the details manually.`)

        return
      }

      setProductNotFoundError('')

      if (productDetails != null) {
        setProductDetails(productInfo)
      }
    }
  }

  const onScan = (code: any) => {
    setLookupField(code)
    setLookupButtonClicked(true)
  }

  return (
    <>
      <Container>
        <ScanButton
          onScan={onScan}
          onInvalid={(msg: any) => {
            setErrorMessage(msg)
            setIsError(true)
          }}
        />
        <TextInputField
          className="ln-u-margin-top*2"
          name={'product-lookup-input'}
          label={'Or enter a SKU or EAN'}
          onChange={handleLookupChange}
          value={lookupField}
          error={productNotFoundError || (isError ? errorMessage : '')}
          placeholder={'Enter SKU / EAN number ...'}
        />
        <OutlinedButton
          className="ln-u-margin-bottom*2"
          fullWidth
          onClick={() => {
            setLookupButtonClicked(true)
          }}
        >
          {'Lookup product'}
        </OutlinedButton>
        <div className="ln-u-margin-bottom">
          <Body1 id="lookup-body-text">
            If a product cannot be found, enter a name, price and quantity manually.
          </Body1>
        </div>
        <div className="ln-u-margin-bottom*2">
          <Body1 id="lookup-body-text">
            If you’re unable to determine what products were taken, please provide a description in the “Product name” field, enter a price of “£0.00” and Quantity “0”.
          </Body1>
        </div>
        {productDetails?.productName ? (
          <>
            <ComponentSwitch
              id="product_name_label"
              type="label"
              value="Product name (on packaging)"
              key={`${key}-product_name_label`}
            />
            <ComponentSwitch
              id="product_name_value"
              type="text"
              value={value?.productName}
              key={`${key}-product_name_value`}
            />
          </>
        ) : (
          <ComponentSwitch
            id={productName.id}
            type="input"
            label="Product name (on packaging)"
            placeholder="Enter product info ..."
            answer={value?.product_name}
            key={`${key}-product_name`}
            prefix=""
            error={productName.error}
          />
        )}
        {productDetails?.sku && (
          <>
            <ComponentSwitch
              id="sku_label"
              type="label"
              value="SKU number"
              key={`${key}-sku_label`}
            />
            <ComponentSwitch
              id="sku_value"
              type="text"
              value={value?.sku}
              key={`${key}-sku_value`}
            />
          </>
        )}
        {productDetails?.price ? (
          <>
            <ComponentSwitch
              id="price_label"
              type="label"
              value="Price"
              key={`${key}-price_label`}
            />
            <ComponentSwitch
              id="price_value"
              type="text"
              value={`£${value?.price}`}
              key={`${key}-price_value`}
            />
          </>
        ) : (
          <ComponentSwitch
            id={price.id}
            type="input"
            label="Price"
            placeholder="Enter price ..."
            answer={value?.product_price}
            key={`${key}-product_price`}
            prefix="£"
            error={price.error}
          />
        )}
        <ComponentSwitch
          id={quantity.id}
          type="input"
          label="Quantity"
          placeholder="Enter quantity ..."
          answer={value?.product_quantity}
          key={`${key}-product_quantity`}
          prefix=""
          error={quantity.error}
        />
        <ComponentSwitch
          id={putBackOnSale.id}
          type="checkboxList"
          options={[{ value: 'true', label: 'Product was involved in incident, but was put back on sale' }]}
          defaultValue=''
          key={`${key}-product_put_back_on_sale`}
          error={putBackOnSale.error}
        />
      </Container>
    </>
  )
}

export default ProductLookupComponent